import {
  COMPANY_PERSONAL_STRUCTURE_TYPES,
  USER_ROLES,
  VERIFICATION_PROCESS_TYPES,
  VERIFICATION_STATES,
  VERIFIED_VERIFICATION_STATES,
} from '@/constants'
import helpers from '@/utils/helpers'
import { isPersonalOrganizationNoBox } from '@/utils/helpers/onboarding.ts'
import { useApi } from '@/composables/api'
import type { UserFull, UserMeOrganization } from '@deepcloud/api-deep-admin'
import type { OverviewQueryParams } from '@deepcloud/api-deep-box'

export const useDeepAdminUsersMeStore = defineStore('deepAdminUsersMe', () => {
  const { deepAdminAPI, deepBoxAPI, deepAdminBackendAPI } = useApi()

  const user = ref<UserFull>()
  const organizations = ref<UserMeOrganization[]>([])
  const selectedOrganization = ref<UserMeOrganization>()
  const switchOrganizationPending = ref(false)
  const fetchUserOrganizationsPending = ref(false)
  const fetchUsersMePending = ref(false)
  const fetchUserMePending = ref(false)
  const fetchUserMeFulfilled = ref(false)

  const getUserOrganizationsPersonal = computed(() => {
    const organizations: UserMeOrganization[] = user.value?.companies || []
    return organizations.filter(
      (org) =>
        org.structure &&
        COMPANY_PERSONAL_STRUCTURE_TYPES.includes(org.structure),
    )
  })

  const getUserSelectableOrganizations = computed(() => {
    if (organizations.value.length === 1) {
      return organizations.value
    }

    let verifiedOrganizations = organizations.value.filter((organization) =>
      VERIFIED_VERIFICATION_STATES.includes(organization?.verification_state),
    )
    const verificationInProgressOrganizations = organizations.value.filter(
      (organization) =>
        organization.verification_process_status ===
        VERIFICATION_PROCESS_TYPES.VERIFICATION_PROCESS_TYPE_IN_PROGRESS,
    )
    const verifiedPersonalOrganizations = verifiedOrganizations.filter(
      (organization) => helpers.isPersonalStructure(organization),
    )
    const verifiedBusinessOrganizations = verifiedOrganizations.filter(
      (organization) => !helpers.isPersonalStructure(organization),
    )
    verifiedOrganizations = verifiedBusinessOrganizations.concat(
      verifiedPersonalOrganizations,
    )
    const unverifiedOrganizations = organizations.value.filter(
      (organization) =>
        organization?.verification_state ===
        VERIFICATION_STATES.VERIFICATION_STATE_NONE,
    )
    const unverifiedPersonalOrganizations = unverifiedOrganizations.filter(
      (organization) => helpers.isPersonalStructure(organization),
    )
    const unverifiedBusinessOrganizations = unverifiedOrganizations.filter(
      (organization) => !helpers.isPersonalStructure(organization),
    )

    let selectableOrganizations: UserMeOrganization[] = []
    if (verifiedOrganizations.length > 0) {
      selectableOrganizations = verifiedOrganizations.concat(
        unverifiedBusinessOrganizations,
      )
    } else {
      selectableOrganizations = unverifiedBusinessOrganizations.concat(
        selectableOrganizations,
      )
    }

    unverifiedPersonalOrganizations.forEach((organization) => {
      if (
        organization.hasSharedBoxes ||
        (verifiedOrganizations.length === 0 &&
          verificationInProgressOrganizations.length > 0)
      ) {
        selectableOrganizations.push(organization)
      }
    })

    return selectableOrganizations.sort((a, b) => {
      const displayNameA = a.display_name.toLowerCase()
      const displayNameB = b.display_name.toLowerCase()
      return displayNameA > displayNameB
        ? 1
        : displayNameB > displayNameA
          ? -1
          : 0
    })
  })

  function getUserPersonalOrg(organizations: UserMeOrganization[]) {
    return organizations.filter((org) =>
      org.roles.some((r) => [USER_ROLES.USER_ROLE_MANAGE_COMPANY].includes(r)),
    )
  }

  const personalOrganization = computed(() => {
    let list = getUserOrganizationsPersonal.value as UserMeOrganization[]

    list = getUserPersonalOrg(list)

    return list.length > 0 ? list[0] : null
  })

  const _isPersonalOrganizationNoBox = computed(() => {
    const userPersonalOrganization =
      personalOrganization.value as UserMeOrganization | null

    return userPersonalOrganization
      ? isPersonalOrganizationNoBox(userPersonalOrganization)
      : undefined
  })

  const currentOrganization = computed(() => {
    return organizations.value.find(
      (organization) =>
        organization.group_id === selectedOrganization.value?.group_id,
    )
  })

  const getOrganizationById = (id: string) => {
    return organizations.value.find(
      (organization) => organization.group_id === id,
    )
  }

  const getUserSelectableOrganizationById = (id: string) => {
    return getUserSelectableOrganizations.value.find(
      (organization) => organization.group_id === id,
    )
  }

  const selectedOrganizationId = computed(
    () => selectedOrganization.value?.group_id,
  )

  async function fetchUsersMe() {
    fetchUsersMePending.value = true
    try {
      const res = await deepAdminAPI.value.usersMe.get()
      user.value = res?.data
      fetchUsersMePending.value = false
      return Promise.resolve(res)
    } catch (error) {
      console.log(error)
      return Promise.reject(error)
    } finally {
      fetchUsersMePending.value = false
    }
  }

  async function fetchUserOrganizations() {
    fetchUserOrganizationsPending.value = true
    try {
      const res = await fetchUsersMe()
      const resData = res?.data

      // Check if personal organization need to be filtered
      const userOrganizations = [...resData.companies]
      // update state
      organizations.value = [...userOrganizations]
      let userPersonalOrganizations = userOrganizations.filter((userOrg) => {
        return (
          userOrg.structure &&
          COMPANY_PERSONAL_STRUCTURE_TYPES.includes(userOrg.structure)
        )
      })
      // filter user no-box org if is verified and if the structure still be no-box
      let userPersonalOrganization: UserMeOrganization | null = null

      // filter all other personal org out ( users can only have 1 personal with role
      userPersonalOrganizations = getUserPersonalOrg(userPersonalOrganizations)

      if (userPersonalOrganizations.length === 1) {
        userPersonalOrganization =
          userPersonalOrganizations[0] as UserMeOrganization
      }

      if (userPersonalOrganization !== null) {
        if (
          _isPersonalOrganizationNoBox.value &&
          userOrganizations.length > 1
        ) {
          const personalOrgIdx = userOrganizations.findIndex(
            (userOrg) =>
              userOrg.group_id === userPersonalOrganization?.group_id,
          )
          if (personalOrgIdx !== -1) {
            const params: OverviewQueryParams = {
              'deepBoxes.boxes.limit': 1,
              'companyId': userPersonalOrganization.group_id,
            }

            const userPersonalOrganizationOverview =
              await deepBoxAPI.value.overview.get(params)
            const userPersonalOrganizationSharesBox =
              userPersonalOrganizationOverview?.data?.sharedWithMe?.boxes || []
            userOrganizations[personalOrgIdx].hasSharedBoxes =
              userPersonalOrganizationSharesBox.length > 0
            userOrganizations[personalOrgIdx].hasPrivateBox =
              userPersonalOrganizationOverview.data.privateBox !== undefined
          }
        }
      }
      organizations.value = [...userOrganizations]

      return Promise.resolve(res)
    } catch (error) {
      console.log(error)
      return Promise.reject(error)
    } finally {
      fetchUserOrganizationsPending.value = false
    }
  }

  function selectOrganization(organization: UserMeOrganization) {
    selectedOrganization.value = { ...organization }
  }

  async function fetchOrgBetaFeatures(orgId: string) {
    if (!orgId) return

    try {
      const { data } =
        await deepAdminBackendAPI.value.accountsBetaFeaturesV2.get(orgId)

      if (data) {
        const betaFeatures = data.items?.map((i) => i.featureName)

        organizations.value = organizations.value?.map((o) =>
          o.group_id === orgId
            ? {
                ...o,
                metadata: {
                  ...o.metadata,
                  beta_features: betaFeatures,
                },
              }
            : o,
        )

        // this can be called for orgs that are never "selected" (e.g. a private box's org)
        if (selectedOrganization.value?.group_id === orgId) {
          selectedOrganization.value = {
            ...selectedOrganization.value,
            metadata: {
              ...selectedOrganization.value.metadata,
              beta_features: betaFeatures,
            },
          }
        }
      }
    } catch (e: unknown) {
      console.error(e)
    }
  }

  watch(
    () => selectedOrganizationId.value,
    (newValue, oldValue) => {
      if (newValue && (!oldValue || newValue !== oldValue)) {
        void fetchOrgBetaFeatures(newValue)
      }
    },
  )

  return {
    // state
    user,
    organizations,
    selectedOrganization,
    selectedOrganizationId,
    switchOrganizationPending,
    fetchUserOrganizationsPending,
    fetchUsersMePending,
    fetchUserMePending,
    fetchUserMeFulfilled,
    // getters
    getUserOrganizationsPersonal,
    getUserSelectableOrganizations,
    personalOrganization,
    currentOrganization,
    getOrganizationById,
    getUserSelectableOrganizationById,
    // actions
    fetchUsersMe,
    fetchUserOrganizations,
    selectOrganization,
    fetchOrgBetaFeatures,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useDeepAdminUsersMeStore, import.meta.hot),
  )
}
